import type { TooltipPlacementTypes } from '@etg/wings';
import { Inline, Stack, Text, Tooltip, vars } from '@etg/wings';
import { Info } from '@phosphor-icons/react';
import { css, cx, etiColors, resetButtonStyle } from '@eti/styles';
import { forwardRef, type ReactNode } from 'react';
import QuickSortPriceAndTime from './QuickSortPriceAndTime';

const quickSortButtonStyles = (isSelected: boolean) => css`
  ${resetButtonStyle}
  align-items: center;
  background-color: ${etiColors.white};
  border-bottom: 4px solid ${isSelected ? vars.colors.inputs.main : 'transparent'};
  border-right: 1px solid ${vars.colors.divider};
  color: ${isSelected ? vars.colors.inputs.main : etiColors.black};
  display: block;
  flex-basis: 100%;
  padding: 16px;
  text-align: start;
  width: 100%;

  &:focus-visible {
    outline: 2px solid ${vars.colors.focusRing};
    z-index: 1;
  }

  &:hover {
    background: ${vars.colors.inputs.hover};
  }
`;

const inlineStyles = css`
  justify-content: space-between;
`;

interface QuickSortButtonProps {
  className?: string;
  desc?: string;
  formattedPrice?: string;
  id?: string;
  isSelected: boolean;
  label: string;
  onClick: (type: string) => void;
  rightSection?: ReactNode;
  time?: string;
  tooltipPlacement?: TooltipPlacementTypes;
  type: string;
}

export const QuickSortButton = forwardRef<HTMLButtonElement, QuickSortButtonProps>(
  (
    {
      className,
      desc,
      formattedPrice,
      id,
      isSelected,
      label,
      onClick,
      rightSection,
      time,
      tooltipPlacement = 'bottom',
      type,
      ...rest
    },
    ref?,
  ) => {
    const buttonContent = (
      <button
        aria-checked={isSelected}
        className={cx(quickSortButtonStyles(isSelected), className)}
        data-testid="result-quick-sort-button"
        id={id}
        onClick={() => onClick(type)}
        ref={ref}
        role="radio"
        type="button"
        {...rest}
      >
        <Inline alignY="center" className={inlineStyles} noWrap spacing={4}>
          <Stack spacing={4}>
            {desc ? (
              <Inline alignY="center" spacing={4}>
                <Text variant="14-bold">{label}</Text>
                <Info
                  aria-hidden
                  color={vars.colors.signal.information.foreground}
                  role="presentation"
                  size={20}
                  weight="fill"
                />
              </Inline>
            ) : (
              <Text variant="14-bold">{label}</Text>
            )}
            {formattedPrice && (
              <QuickSortPriceAndTime formattedPrice={formattedPrice} time={time} />
            )}
          </Stack>
          {rightSection}
        </Inline>
      </button>
    );

    return desc ? (
      <Tooltip content={desc} placement={tooltipPlacement} title={desc ?? ''}>
        {buttonContent}
      </Tooltip>
    ) : (
      buttonContent
    );
  },
);

export default QuickSortButton;
